import { TextInput } from "components/core";
import { useFormik } from "formik";
import { FC } from "react";
import { wrapClick } from "utils";
import moment from "moment";
import {IMeterDeliveryRequestFormSchema, ContractInfoFormSchema} from "./schema";
import {CheckCircleIcon} from "@heroicons/react/20/solid";

interface ContractInfoFormProps {
  handleNext: (values: IMeterDeliveryRequestFormSchema["contractInfo"]) => void;
  handlePrevious: () => void;
  initialValues: IMeterDeliveryRequestFormSchema["contractInfo"];
  values: IMeterDeliveryRequestFormSchema;
  handleCancel: () => void;
  meterContractor: any;
  refetchMeterContractor: () => void;
}

const ContractInfoForm: FC<ContractInfoFormProps> = ({
  initialValues,
  handleNext,
  handleCancel,
  meterContractor,
}) => {

  const form = useFormik({
    initialValues,
    validationSchema: ContractInfoFormSchema,
    onSubmit: (values) => {
      handleNext(values);
    },
    onReset: () => {
      handleCancel?.();
    },
  });

  return (
    <div className="flex-1 flex flex-col overflow-hidden">
      <div className="space-y-6 divide-y divide-gray-200 p-6 flex-1 overflow-y-auto">
        <div>
          <span className="text-xs font-light">Contract Information</span>
          <div className="grid grid-cols-3 gap-6 mt-2">
            <div className="">
              <TextInput
                id="contractNumber"
                label="Contract Number"
                type="text"
                placeholder="e.g. 1234"
                {...form}
              />
            </div>

            <div className="col-span-2">
              <TextInput
                id="contractTitle"
                label="Contract Title"
                type="text"
                placeholder="e.g. FAT Meter Supplier Contract"
                {...form}
              />
            </div>
          </div>
        </div>
        <div className="pt-6">
          <span className="text-xs font-light">Testing Facility</span>
          <div className="mt-2 grid grid-cols-2 gap-x-4">
            <button
              // Disabled if the meter contractor does not have a test facility
              disabled={!Boolean(meterContractor?.testingFacility?.name)}
              onClick={() => form.setFieldValue("testAtOwnFacility", true)}
              className="relative cursor-pointer disabled:cursor-not-allowed rounded-lg border border-gray-300 text-gray-900bg-white p-4 shadow-sm hover:outline-none hover:border-primary-600 hover:ring-1 hover:ring-primary-600 disabled:bg-gray-100 disabled:text-gray-400 disabled:border-gray-400 "
            >
              <h3 className="text-base font-medium leading-6 ">
                Meter Supplier Testing Facility
              </h3>
              {form.values.testAtOwnFacility && (
                <>
                  <CheckCircleIcon className="h-5 w-5 text-primary-600 absolute right-4 top-[1.2rem]"/>
                  <span
                    className="pointer-events-none absolute -inset-px rounded-lg border-2 border-transparent border-primary-600 ring-1 ring-primary-600"/>
                </>
              )}
            </button>
            <button
              onClick={() => form.setFieldValue("testAtOwnFacility", false)}
              className="relative cursor-pointer rounded-lg border border-gray-300 bg-white p-4 shadow-sm hover:outline-none hover:border-primary-600 hover:ring-1 hover:ring-primary-600"
            >
              <h3 className="text-base font-medium leading-6 text-gray-900">
                ECG Meter Testing Facility
              </h3>
              {!form.values.testAtOwnFacility && (
                <>
                  <CheckCircleIcon className="h-5 w-5 text-primary-600 absolute right-4 top-[1.2rem]"/>
                  <span
                    className="pointer-events-none absolute -inset-px rounded-lg border-2 border-transparent border-primary-600 ring-1 ring-primary-600"/>
                </>
              )}
            </button>
          </div>
        </div>

        <div className="pt-6">
          <span className="text-xs font-light">Expected Timeline Information</span>
          <div className="grid grid-cols-2 gap-6 mt-2">
            <div className="">
              <TextInput
                id="proposedStartDate"
                label="Proposed Start Date"
                type="date"
                min={moment().format("YYYY-MM-DD")}
                max={moment().add(12, "months").format("YYYY-MM-DD")}
                placeholder="e.g. 06/03/1957"
                {...form}
              />
            </div>
            <div className="">
              <TextInput
                id="estimatedProductionCompletionDate"
                label="Expected Production Completion Date"
                type="date"
                min={moment(form.values.proposedStartDate).format("YYYY-MM-DD")}
                max={moment().add(12, "months").format("YYYY-MM-DD")}
                placeholder="e.g. 06/03/1957"
                {...form}
              />
            </div>
          </div>
        </div>
      </div>
      <div className="bg-gray-50 dark:bg-gray-800 px-4 py-3 sm:py-4 sm:px-6 sm:flex sm:flex-row-reverse border-t border-gray-200">
        <button
          type="button"
          data-invalid={!form.isValid}
          onClick={wrapClick(form.handleSubmit)}
          className="w-full inline-flex justify-center data-invalid:bg-gray-200 data-invalid:border-gray-400 data-invalid:cursor-not-allowed data-invalid:text-gray-600 rounded-md border border-transparent shadow-sm px-4 py-2 bg-primary-600 text-base font-medium text-white hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 sm:ml-3 sm:w-auto sm:text-sm"
        >
          Next
        </button>
        <button
          type="button"
          className="mt-3 w-full inline-flex justify-center rounded-md border border-gray-300 dark:border-gray-600 shadow-sm px-4 py-2 bg-white dark:bg-gray-900 text-base font-medium text-gray-700 dark:text-gray-200 hover:bg-gray-50 hover:dark:bg-gray-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 sm:mt-0 sm:ml-3 sm:w-auto sm:text-sm"
          onClick={wrapClick(form.resetForm)}
        >
          Cancel
        </button>
      </div>
    </div>
  );
};

export default ContractInfoForm;
